<template>
  <v-card class="employee-timesheet" outlined>
    <v-card-title class="timesheet-header">
      <div class="timesheet-header__title">
        <v-icon size="20" class="mr-2">mdi-clock-outline</v-icon>
        <span class="text-subtitle-1 font-weight-medium">{{ title || t("employeeTimesheet.title") }}</span>
        <v-chip color="primary" class="ml-2">
          {{ formattedSelectedDate }}
        </v-chip>
      </div>
      <div class="timesheet-header__actions">
        <v-btn icon variant="text" size="small" @click="goToPreviousDay">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon variant="text" size="small" @click="goToNextDay">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn variant="text" size="small" @click="goToToday">{{ t("employeeTimesheet.today") }}</v-btn>
        <v-menu v-model="calendarMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
          <template #activator="{ props }">
            <v-btn v-bind="props" variant="text" size="small">
              <v-icon start>mdi-calendar</v-icon>
              {{ t("employeeTimesheet.pickDay") }}
            </v-btn>
          </template>
          <v-date-picker
            :model-value="selectedDate"
            color="primary"
            hide-header
            :weekdays="[1, 2, 3, 4, 5, 6, 0]"
            :first-day-of-week="1"
            @update:model-value="handleDateSelection"
          />
        </v-menu>
        <v-btn
          v-if="canManage"
          color="primary"
          size="small"
          variant="tonal"
          class="ml-2"
          @click="openCreateDialog"
        >
          <v-icon start>mdi-plus</v-icon>
          {{ t("employeeTimesheet.addEvent") }}
        </v-btn>
      </div>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="timesheet-body">
      <v-alert
        v-if="errorMessage"
        type="error"
        density="comfortable"
        class="mb-3"
      >
        {{ errorMessage }}
      </v-alert>

      <v-skeleton-loader
        v-if="loading"
        type="list-item-two-line"
        class="mb-4"
        :loading="loading"
        :rows="3"
      />

      <div v-else>
        <div v-if="smartTrackerVisible" class="timesheet-smart-tracker mb-4">
          <div class="d-flex align-center justify-space-between ga-2 mb-2">
            <div class="d-flex align-center ga-2">
              <v-icon size="18">mdi-timer-outline</v-icon>
              <span class="text-subtitle-2 ncrm-text-strong">
                {{ t("employeeTimesheet.smartTracker.title") }}
              </span>
            </div>
            <v-btn
              size="small"
              variant="text"
              color="primary"
              :loading="smartTrackerLoading"
              @click="fetchSmartTrackerSummary"
            >
              <v-icon start size="16">mdi-refresh</v-icon>
              {{ t("employeeTimesheet.smartTracker.refresh") }}
            </v-btn>
          </div>

          <v-alert
            v-if="smartTrackerError"
            type="error"
            density="comfortable"
            class="mb-2"
          >
            {{ smartTrackerError }}
          </v-alert>

          <v-alert
            v-else-if="!smartTrackerSummary.settings.smart_tracker_enabled"
            type="info"
            density="comfortable"
            class="mb-2"
          >
            {{ t("employeeTimesheet.smartTracker.disabled") }}
          </v-alert>

          <v-row v-if="smartTrackerSummary.settings.smart_tracker_enabled" dense class="mb-2">
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="timesheet-smart-tracker__kpi">
                <v-card-text>
                  <div class="text-caption text-medium-emphasis">
                    {{ t("employeeTimesheet.smartTracker.sessionTimer") }}
                  </div>
                  <div class="text-h6 ncrm-text-strong">
                    {{ formatDuration(smartTrackerSessionSecondsDisplay) }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="timesheet-smart-tracker__kpi">
                <v-card-text>
                  <div class="text-caption text-medium-emphasis">
                    {{ t("employeeTimesheet.smartTracker.dayTimer") }}
                  </div>
                  <div class="text-h6 ncrm-text-strong">
                    {{ formatDuration(smartTrackerDaySecondsDisplay) }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-alert
            v-if="smartTrackerSummary.settings.smart_tracker_enabled && !smartTrackerSessions.length && !smartTrackerLoading"
            type="info"
            density="comfortable"
          >
            {{ t("employeeTimesheet.smartTracker.empty") }}
          </v-alert>

          <v-list
            v-if="smartTrackerSummary.settings.smart_tracker_enabled && smartTrackerSessions.length"
            class="timesheet-smart-tracker__list"
            density="comfortable"
          >
            <v-list-item
              v-for="session in smartTrackerSessions"
              :key="session.id"
              class="timesheet-smart-tracker__item"
            >
              <template #prepend>
                <v-avatar size="34" color="primary" variant="tonal">
                  <v-icon size="18">mdi-timer-sand</v-icon>
                </v-avatar>
              </template>
              <template #title>
                <div class="d-flex flex-wrap align-center ga-2">
                  <span class="font-weight-medium">
                    {{ formatSessionRange(session) }}
                  </span>
                  <v-chip size="x-small" :color="smartTrackerStateChip(session.state).color">
                    {{ smartTrackerStateChip(session.state).label }}
                  </v-chip>
                  <v-chip size="x-small" variant="outlined" color="primary">
                    {{ formatDuration(session.runtime_seconds || session.duration_seconds) }}
                  </v-chip>
                </div>
              </template>
              <template #subtitle>
                <div class="timesheet-smart-tracker__label">
                  <span class="text-caption text-medium-emphasis">
                    {{ t("employeeTimesheet.smartTracker.label") }}:
                  </span>
                  <span class="text-body-2">
                    {{ smartTrackerSessionDisplayLabel(session) }}
                  </span>
                </div>
              </template>
              <template #append>
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  color="primary"
                  @click="openSmartTrackerLabelDialog(session)"
                >
                  <v-icon size="18">mdi-pencil</v-icon>
                </v-btn>
              </template>
            </v-list-item>
          </v-list>
        </div>

        <div v-if="events.length === 0" class="timesheet-empty">
          <v-icon size="36" color="primary" class="mb-2">mdi-calendar-blank-outline</v-icon>
          <p class="text-body-2 text-medium-emphasis mb-1">{{ t("employeeTimesheet.empty.title") }}</p>
          <p v-if="canManage" class="text-caption text-medium-emphasis">
            {{ t("employeeTimesheet.empty.hint") }}
          </p>
        </div>
        <v-list v-else class="timesheet-list" lines="two" density="comfortable">
          <v-list-item
            v-for="event in orderedEvents"
            :key="event.id"
            class="timesheet-list__item"
          >
            <template #prepend>
              <v-avatar
                size="36"
                :color="event.is_auto ? 'primary' : 'grey-lighten-3'"
                class="mr-3"
              >
                <v-icon :color="event.is_auto ? 'white' : 'primary'">{{ eventTypeConfig(event.event_type).icon }}</v-icon>
              </v-avatar>
            </template>
            <template #title>
              <div class="d-flex align-center">
                <span class="font-weight-medium mr-2">{{ eventTypeConfig(event.event_type).label }}</span>
                <v-chip
                  size="x-small"
                  :color="event.is_auto ? 'primary' : 'grey'"
                  variant="outlined"
                >
                  {{ event.is_auto ? t("employeeTimesheet.auto") : t("employeeTimesheet.manual") }}
                </v-chip>
              </div>
            </template>
            <template #subtitle>
              <div class="timesheet-item__meta">
                <span>
                  <v-icon size="16" class="mr-1">mdi-clock-outline</v-icon>
                  {{ formatEventTime(event.event_time, event.source === "auto_ui") }}
                </span>
                <span v-if="event.source" class="ml-3 text-caption text-medium-emphasis">
                  {{ t("employeeTimesheet.source") }}: {{ formatSource(event.source) }}
                </span>
              </div>
            </template>
            <template #append>
              <div v-if="canManage" class="d-flex align-center">
                <v-btn icon size="small" variant="text" @click="openEditDialog(event)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon size="small" variant="text" color="error" @click="deleteEvent(event)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>
          </v-list-item>
        </v-list>

      </div>
    </v-card-text>
  </v-card>

  <v-dialog v-model="dialog.open" max-width="420px">
    <v-card>
      <v-card-title>{{ dialog.mode === 'create' ? t("employeeTimesheet.dialog.addTitle") : t("employeeTimesheet.dialog.editTitle") }}</v-card-title>
      <v-card-text>
        <v-form @submit.prevent="saveEvent">
          <v-select
            :label="t('employeeTimesheet.fields.type')"
            :items="eventTypeOptions"
            item-title="label"
            item-value="value"
            v-model="dialog.form.event_type"
            class="mb-3"
            required
          ></v-select>
          <v-menu
            v-model="timeMenu"
            :close-on-content-click="false"
            max-width="320"
            transition="scale-transition"
          >
            <template #activator="{ props }">
              <v-text-field
                v-bind="props"
                :label="t('employeeTimesheet.fields.time')"
                :model-value="dialog.form.time"
                readonly
                variant="outlined"
                density="comfortable"
                :rules="[timeFieldRule]"
                append-inner-icon="mdi-clock-outline"
              ></v-text-field>
            </template>
            <v-card>
              <v-time-picker
                v-model="timePickerValue"
                format="24hr"
                scrollable
                @update:model-value="onTimeSelected"
              />
              <v-card-actions class="justify-end">
                <v-btn variant="text" @click="timeMenu = false">{{ t("app.actions.cancel") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
          <v-switch
            v-if="dialog.mode === 'edit'"
            v-model="dialog.form.is_auto"
            inset
            color="primary"
            hide-details
            class="mt-3"
            :label="t('employeeTimesheet.fields.autoLabel')"
          ></v-switch>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="dialog.open = false">{{ t("app.actions.cancel") }}</v-btn>
        <v-btn color="primary" :loading="dialog.loading" @click="saveEvent">
          {{ t("app.actions.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="smartTrackerLabelDialog.open" max-width="560px">
    <v-card>
      <v-card-title>{{ t("employeeTimesheet.smartTracker.editDialog.title") }}</v-card-title>
      <v-card-text>
        <v-alert
          v-if="smartTrackerLabelDialog.error"
          type="error"
          density="comfortable"
          class="mb-3"
        >
          {{ smartTrackerLabelDialog.error }}
        </v-alert>
        <v-textarea
          v-model="smartTrackerLabelDialog.value"
          :label="t('employeeTimesheet.smartTracker.editDialog.label')"
          :counter="250"
          :maxlength="250"
          auto-grow
          rows="3"
        />
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" :disabled="smartTrackerLabelDialog.loading" @click="smartTrackerLabelDialog.open = false">
          {{ t("app.actions.cancel") }}
        </v-btn>
        <v-btn color="primary" :loading="smartTrackerLabelDialog.loading" @click="saveSmartTrackerLabel">
          {{ t("app.actions.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-snackbar v-model="smartTrackerSnackbar.show" :color="smartTrackerSnackbar.color" :timeout="3000">
    {{ smartTrackerSnackbar.message }}
  </v-snackbar>
</template>

<script>
import axios from "axios";
import { defineComponent, computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { VTimePicker } from "vuetify/labs/VTimePicker";
import { formatDisplayDate, formatDisplayTime, formatDisplayWeekday } from "@/Utile/displayFormat.mjs";

const EVENT_TYPES = [
  { value: "intrare", labelKey: "employeeTimesheet.eventTypes.checkIn", icon: "mdi-login" },
  { value: "pauza_start", labelKey: "employeeTimesheet.eventTypes.breakStart", icon: "mdi-coffee" },
  { value: "pauza_end", labelKey: "employeeTimesheet.eventTypes.breakEnd", icon: "mdi-coffee-outline" },
  { value: "iesire", labelKey: "employeeTimesheet.eventTypes.checkOut", icon: "mdi-logout" },
];

const parseEmployeeId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export default defineComponent({
  name: "EmployeeTimesheet",
  components: {
    VTimePicker,
  },
  props: {
    employeeId: {
      type: [Number, String],
      required: true,
    },
    companyId: {
      type: [Number, String],
      default: null,
    },
    title: {
      type: String,
      default: "Pontaj zilnic",
    },
    canManage: {
      type: Boolean,
      default: false,
    },
    initialDate: {
      type: String,
      default: () => new Date().toISOString().split("T")[0],
    },
  },
  setup(props) {
    const { t, locale } = useI18n();
    const events = ref([]);
    const loading = ref(false);
    const errorMessage = ref("");
    const selectedDate = ref(normalizeToDate(props.initialDate));
    const calendarMenu = ref(false);

    const dialog = ref({
      open: false,
      mode: "create",
      loading: false,
      form: {
        id: null,
        event_type: EVENT_TYPES[0].value,
        time: "09:00",
        is_auto: false,
        source: "manual",
      },
    });

    const timeMenu = ref(false);
    const timePickerValue = ref("09:00");

    const loggedEmployeeId = ref(parseEmployeeId(localStorage.getItem("user-employee-id")));
    const smartTrackerVisible = computed(
      () =>
        loggedEmployeeId.value !== null &&
        parseEmployeeId(props.employeeId) !== null &&
        parseEmployeeId(props.employeeId) === loggedEmployeeId.value
    );
    const smartTrackerSummary = ref({
      date: null,
      session_timer_seconds: 0,
      today_total_seconds: 0,
      sessions_count: 0,
      sessions: [],
      settings: {
        smart_tracker_enabled: false,
      },
    });
    const smartTrackerLoading = ref(false);
    const smartTrackerError = ref("");
    const smartTrackerLoadedAt = ref(0);
    const smartTrackerTickerNow = ref(Date.now());
    const smartTrackerTickTimer = ref(null);
    const smartTrackerRefreshTimer = ref(null);
    const smartTrackerLabelDialog = ref({
      open: false,
      loading: false,
      error: "",
      sessionId: null,
      value: "",
    });
    const smartTrackerSnackbar = ref({
      show: false,
      color: "info",
      message: "",
    });

    const selectedDateIso = computed(() => formatIsoDate(selectedDate.value));

    const formattedSelectedDate = computed(() => formatLongDate(selectedDate.value));

    const canManage = computed(() => props.canManage);
    const cardTitle = computed(() => props.title);

    const orderedEvents = computed(() =>
      [...events.value].sort((a, b) => {
        const timeA = a?.event_time ? new Date(a.event_time).getTime() : 0;
        const timeB = b?.event_time ? new Date(b.event_time).getTime() : 0;
        if (timeA !== timeB) {
          return timeA - timeB;
        }
        const idA = Number(a?.id || 0);
        const idB = Number(b?.id || 0);
        return idA - idB;
      })
    );

    const smartTrackerSessions = computed(() => {
      const items = Array.isArray(smartTrackerSummary.value?.sessions)
        ? smartTrackerSummary.value.sessions
        : [];
      return [...items].sort((left, right) => {
        const leftTime = left?.started_at ? new Date(left.started_at).getTime() : 0;
        const rightTime = right?.started_at ? new Date(right.started_at).getTime() : 0;
        return rightTime - leftTime;
      });
    });

    const smartTrackerStatusActive = computed(() =>
      smartTrackerSessions.value.some((session) => String(session?.state || "").toLowerCase() === "active")
    );

    const smartTrackerSessionSecondsDisplay = computed(() => {
      const base = Number(smartTrackerSummary.value?.session_timer_seconds || 0);
      if (!smartTrackerStatusActive.value) return base;
      const loadedAt = Number(smartTrackerLoadedAt.value || 0);
      if (!loadedAt) return base;
      const delta = Math.max(0, Math.floor((smartTrackerTickerNow.value - loadedAt) / 1000));
      return base + delta;
    });

    const smartTrackerDaySecondsDisplay = computed(() => {
      const base = Number(smartTrackerSummary.value?.today_total_seconds || 0);
      if (!smartTrackerStatusActive.value) return base;
      const loadedAt = Number(smartTrackerLoadedAt.value || 0);
      if (!loadedAt) return base;
      const delta = Math.max(0, Math.floor((smartTrackerTickerNow.value - loadedAt) / 1000));
      return base + delta;
    });

    const eventTypeOptions = computed(() =>
      EVENT_TYPES.map((et) => ({
        ...et,
        label: t(et.labelKey),
      }))
    );

    function eventTypeConfig(type) {
      const match = eventTypeOptions.value.find((item) => item.value === type);
      return match || { ...EVENT_TYPES[0], label: t(EVENT_TYPES[0].labelKey) };
    }

    const timeFieldRule = (value) => {
      if (!value) return t("employeeTimesheet.validation.timeRequired");
      return normalizeTimeInput(value) ? true : t("employeeTimesheet.validation.timeFormat");
    };

    function formatSource(source) {
      switch (source) {
        case "auto_job":
          return t("employeeTimesheet.sources.autoJob");
        case "auto_ui":
          return t("employeeTimesheet.sources.autoUi");
        default:
          return t("employeeTimesheet.sources.manual");
      }
    }

    function normalizeToDate(value) {
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return new Date(value.getTime());
      }
      if (!value) {
        return new Date();
      }
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return new Date();
      }
      return parsed;
    }

    function formatLongDate(dateObj) {
      if (!dateObj) return "";
      const date = normalizeToDate(dateObj);
      if (Number.isNaN(date.getTime())) return "";
      return `${formatDisplayWeekday(date, locale?.value)}, ${formatDisplayDate(date)}`;
    }

    function formatIsoDate(dateObj) {
      if (!dateObj) return new Date().toISOString().split("T")[0];
      const date = normalizeToDate(dateObj);
      if (Number.isNaN(date.getTime())) return new Date().toISOString().split("T")[0];
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    function formatEventTime(value, includeSeconds = false) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return formatDisplayTime(date, { seconds: includeSeconds });
    }

    function goToPreviousDay() {
      const date = normalizeToDate(selectedDate.value);
      date.setDate(date.getDate() - 1);
      selectedDate.value = date;
    }

    function goToNextDay() {
      const date = normalizeToDate(selectedDate.value);
      date.setDate(date.getDate() + 1);
      selectedDate.value = date;
    }

    function goToToday() {
      selectedDate.value = new Date();
    }

    function handleDateSelection(value) {
      calendarMenu.value = false;
      selectedDate.value = normalizeToDate(value);
    }

    function openCreateDialog() {
      dialog.value = {
        open: true,
        mode: "create",
        loading: false,
        form: {
          id: null,
          event_type: EVENT_TYPES[0].value,
          time: "09:00",
          is_auto: false,
          source: "manual",
        },
      };
      timePickerValue.value = dialog.value.form.time;
      timeMenu.value = false;
    }

    function openEditDialog(event) {
      dialog.value = {
        open: true,
        mode: "edit",
        loading: false,
        form: {
          id: event.id,
          event_type: event.event_type,
          time: extractTimePart(event.event_time),
          is_auto: Boolean(event.is_auto),
          source: event.source || "manual",
        },
      };
      timePickerValue.value = dialog.value.form.time;
      timeMenu.value = false;
    }

    function extractTimePart(value) {
      if (!value) return "09:00";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value.slice(11, 16);
      }
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    }

    function normalizeTimeInput(value) {
      if (value === null || value === undefined) return null;
      const raw = String(value).trim();
      if (!raw) return null;

      const colonMatch = raw.match(/^(\d{1,2}):(\d{1,2})$/);
      if (colonMatch) {
        const hours = Number(colonMatch[1]);
        const minutes = Number(colonMatch[2]);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }

      const digitsMatch = raw.match(/^(\d{3,4})$/);
      if (digitsMatch) {
        const digits = digitsMatch[1].padStart(4, "0");
        const hours = Number(digits.slice(0, 2));
        const minutes = Number(digits.slice(2, 4));
        if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }

      return null;
    }

    function onTimeSelected(value) {
      const normalized = normalizeTimeInput(value);
      if (normalized) {
        timePickerValue.value = normalized;
        dialog.value.form.time = normalized;
        timeMenu.value = false;
        return;
      }
    }

    function showSmartTrackerSnackbar(message, color = "info") {
      smartTrackerSnackbar.value = {
        show: true,
        color,
        message,
      };
    }

    function formatDuration(value) {
      const total = Math.max(0, Number(value || 0));
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }

    function smartTrackerStateChip(state) {
      const normalized = String(state || "").trim().toLowerCase();
      if (normalized === "active") {
        return {
          color: "success",
          label: t("employeeTimesheet.smartTracker.status.active"),
        };
      }
      if (normalized === "paused") {
        return {
          color: "warning",
          label: t("employeeTimesheet.smartTracker.status.paused"),
        };
      }
      return {
        color: "neutral",
        label: t("employeeTimesheet.smartTracker.status.stopped"),
      };
    }

    function smartTrackerSessionDisplayLabel(session) {
      const manual = String(session?.manual_label || "").trim();
      if (manual) return manual;
      const auto = String(session?.auto_label || "").trim();
      if (auto) return auto;
      return t("employeeTimesheet.smartTracker.emptyLabel");
    }

    function formatSessionRange(session) {
      const start = session?.started_at ? formatEventTime(session.started_at) : "--:--";
      const end =
        session?.ended_at && String(session?.state || "").toLowerCase() === "stopped"
          ? formatEventTime(session.ended_at)
          : t("employeeTimesheet.smartTracker.now");
      return `${start} - ${end}`;
    }

    async function fetchSmartTrackerSummary() {
      if (!smartTrackerVisible.value) {
        smartTrackerSummary.value = {
          date: null,
          session_timer_seconds: 0,
          today_total_seconds: 0,
          sessions_count: 0,
          sessions: [],
          settings: { smart_tracker_enabled: false },
        };
        smartTrackerError.value = "";
        return;
      }

      smartTrackerLoading.value = true;
      smartTrackerError.value = "";
      try {
        const { data } = await axios.get("/smart-tracker/me/today", {
          params: {
            date: selectedDateIso.value,
          },
        });
        smartTrackerSummary.value = {
          date: data?.date || selectedDateIso.value,
          session_timer_seconds: Number(data?.session_timer_seconds || 0),
          today_total_seconds: Number(data?.today_total_seconds || 0),
          sessions_count: Number(data?.sessions_count || 0),
          sessions: Array.isArray(data?.sessions) ? data.sessions : [],
          settings: {
            smart_tracker_enabled: Boolean(data?.settings?.smart_tracker_enabled),
            smart_tracker_idle_seconds: Number(data?.settings?.smart_tracker_idle_seconds || 180),
            smart_tracker_prompt_seconds: Number(data?.settings?.smart_tracker_prompt_seconds || 30),
            smart_tracker_auto_label_enabled:
              data?.settings?.smart_tracker_auto_label_enabled === null ||
              data?.settings?.smart_tracker_auto_label_enabled === undefined
                ? true
                : Boolean(data.settings.smart_tracker_auto_label_enabled),
          },
        };
        smartTrackerLoadedAt.value = Date.now();
      } catch (error) {
        console.error("Eroare la încărcarea sesiunilor Smart Tracker:", error);
        smartTrackerError.value = String(
          error?.response?.data?.error || t("employeeTimesheet.smartTracker.errors.load")
        ).trim();
      } finally {
        smartTrackerLoading.value = false;
      }
    }

    function openSmartTrackerLabelDialog(session) {
      smartTrackerLabelDialog.value = {
        open: true,
        loading: false,
        error: "",
        sessionId: Number(session?.id || 0) || null,
        value: String(session?.manual_label || "").trim(),
      };
    }

    async function saveSmartTrackerLabel() {
      const sessionId = Number(smartTrackerLabelDialog.value.sessionId || 0);
      if (!sessionId || smartTrackerLabelDialog.value.loading) return;
      const trimmed = String(smartTrackerLabelDialog.value.value || "").trim();
      if (trimmed.length > 250) {
        smartTrackerLabelDialog.value.error = t("employeeTimesheet.smartTracker.editDialog.maxLength");
        return;
      }

      smartTrackerLabelDialog.value.loading = true;
      smartTrackerLabelDialog.value.error = "";
      try {
        await axios.patch(`/smart-tracker/sessions/${sessionId}/label`, {
          manual_label: trimmed || null,
        });
        smartTrackerLabelDialog.value.open = false;
        await fetchSmartTrackerSummary();
        showSmartTrackerSnackbar(t("employeeTimesheet.smartTracker.editDialog.saved"), "success");
      } catch (error) {
        smartTrackerLabelDialog.value.error = String(
          error?.response?.data?.error || t("employeeTimesheet.smartTracker.editDialog.saveError")
        ).trim();
      } finally {
        smartTrackerLabelDialog.value.loading = false;
      }
    }

    async function fetchEvents() {
      if (!props.employeeId) return;
      loading.value = true;
      errorMessage.value = "";
      try {
        const { data } = await axios.get("/timesheet/events", {
          params: {
            employeeId: props.employeeId,
            date: selectedDateIso.value,
          },
        });
        events.value = Array.isArray(data?.events) ? data.events : [];
      } catch (error) {
        console.error("Eroare la încărcarea evenimentelor de pontaj:", error);
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t("employeeTimesheet.errors.load");
        errorMessage.value = message;
        events.value = [];
      } finally {
        loading.value = false;
      }
    }

    function buildEventDateTime(time) {
      if (!time) return `${selectedDateIso.value}T00:00:00`;
      const sanitized = time.length === 5 ? `${time}:00` : time;
      return `${selectedDateIso.value}T${sanitized}`;
    }

    async function saveEvent() {
      if (dialog.value.loading) return;
      const form = dialog.value.form;
      const normalizedTime = normalizeTimeInput(form.time);
      if (!form.event_type || !normalizedTime) {
        alert(t("employeeTimesheet.validation.timeRequired"));
        return;
      }
      dialog.value.loading = true;
      try {
        if (dialog.value.mode === "create") {
          await axios.post("/timesheet/events", {
            employee_id: Number(props.employeeId),
            event_type: form.event_type,
            event_time: buildEventDateTime(normalizedTime),
            source: "manual",
            is_auto: false,
          });
        } else {
          await axios.put(`/timesheet/events/${form.id}`, {
            event_type: form.event_type,
            event_time: buildEventDateTime(normalizedTime),
            is_auto: form.is_auto,
            source: form.source,
          });
        }
        dialog.value.open = false;
        await fetchEvents();
      } catch (error) {
        console.error("Eroare la salvarea evenimentului de pontaj:", error);
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t("employeeTimesheet.errors.save");
        alert(message);
      } finally {
        dialog.value.loading = false;
      }
    }

    async function deleteEvent(event) {
      if (!confirm(t("employeeTimesheet.confirmDelete"))) return;
      try {
        await axios.delete(`/timesheet/events/${event.id}`);
        await fetchEvents();
      } catch (error) {
        console.error("Eroare la ștergerea evenimentului de pontaj:", error);
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          t("employeeTimesheet.errors.delete");
        alert(message);
      }
    }

    watch(
      () => [props.employeeId, selectedDateIso.value],
      () => {
        fetchEvents();
        fetchSmartTrackerSummary();
      },
      { immediate: false }
    );

    watch(
      () => smartTrackerVisible.value,
      (visible) => {
        if (visible) {
          fetchSmartTrackerSummary();
        }
      }
    );

    watch(
      () => props.initialDate,
      (newDate) => {
        if (newDate) {
          selectedDate.value = normalizeToDate(newDate);
        }
      }
    );

    onMounted(() => {
      fetchEvents();
      fetchSmartTrackerSummary();
      smartTrackerTickTimer.value = setInterval(() => {
        smartTrackerTickerNow.value = Date.now();
      }, 1000);
      smartTrackerRefreshTimer.value = setInterval(() => {
        fetchSmartTrackerSummary();
      }, 20000);
    });

    onBeforeUnmount(() => {
      if (smartTrackerTickTimer.value) {
        clearInterval(smartTrackerTickTimer.value);
        smartTrackerTickTimer.value = null;
      }
      if (smartTrackerRefreshTimer.value) {
        clearInterval(smartTrackerRefreshTimer.value);
        smartTrackerRefreshTimer.value = null;
      }
    });

    return {
      events,
      orderedEvents,
      loading,
      errorMessage,
      t,
      selectedDate,
      formattedSelectedDate,
      calendarMenu,
      eventTypeOptions,
      dialog: dialog,
      canManage,
      title: cardTitle,
      timeFieldRule,
      timeMenu,
      timePickerValue,
      onTimeSelected,
      handleDateSelection,
      formatEventTime,
      formatDuration,
      formatSource,
      eventTypeConfig,
      goToPreviousDay,
      goToNextDay,
      goToToday,
      openCreateDialog,
      openEditDialog,
      saveEvent,
      deleteEvent,
      smartTrackerVisible,
      smartTrackerSummary,
      smartTrackerLoading,
      smartTrackerError,
      smartTrackerSessions,
      smartTrackerSessionSecondsDisplay,
      smartTrackerDaySecondsDisplay,
      smartTrackerStateChip,
      smartTrackerSessionDisplayLabel,
      formatSessionRange,
      fetchSmartTrackerSummary,
      smartTrackerLabelDialog,
      openSmartTrackerLabelDialog,
      saveSmartTrackerLabel,
      smartTrackerSnackbar,
    };
  },
});
</script>

<style scoped>
.employee-timesheet {
  border-radius: var(--app-radius-base);
}
.timesheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.timesheet-header__title {
  display: flex;
  align-items: center;
  gap: 4px;
}
.timesheet-header__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.timesheet-body {
  min-height: 220px;
}
.timesheet-empty {
  text-align: center;
  padding: 24px 12px;
  border: 1px dashed var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  background-color: var(--ncrm-color-bg-card);
}
.timesheet-list {
  border: 1px solid var(--ncrm-color-border-subtle);
  border-radius: var(--app-radius-base);
  padding: 4px;
}
.timesheet-list__item + .timesheet-list__item {
  border-top: 1px solid var(--ncrm-color-border-subtle);
}
.timesheet-item__meta {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--app-text-default);
}
.timesheet-smart-tracker {
  border: 1px solid var(--ncrm-color-border-subtle);
  border-radius: var(--app-radius-base);
  padding: 12px;
  background: var(--ncrm-color-bg-card);
}
.timesheet-smart-tracker__kpi {
  border-radius: var(--app-radius-base);
}
.timesheet-smart-tracker__list {
  border: 1px solid var(--ncrm-color-border-subtle);
  border-radius: var(--app-radius-base);
  padding: 4px;
}
.timesheet-smart-tracker__item + .timesheet-smart-tracker__item {
  border-top: 1px solid var(--ncrm-color-border-subtle);
}
.timesheet-smart-tracker__label {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
</style>
